import { InputBase, makeStyles } from '@material-ui/core';
import type { AutocompleteProps } from '@material-ui/lab/Autocomplete';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { DisplayCheckBox, DisplayRadio } from '@/atoms/Check';
import * as Tags from '@/atoms/Tags/Tags';
import { InputAdornment } from '@/components/InputAdornment/InputAdornment';
import { ListOverflow } from '@/components/ListOverflow';
import AddSVG from '@/icons/add-lg.svg?react';

export const NeedlAutocomplete = <T, Multiple extends boolean = true>({
  id,
  open,
  onClose,
  multiple,
  disablePortal = false,
  disableCloseOnSelect = true,
  handleHomeEndKeys = true,
  clearOnBlur = true,
  options,
  onChange,
  onKeyDown,
  value,
  getOptionLabel,
  renderOption,
  placeholder,
  filterOptions,
  endAdornment,
  noOptionsText,
  getOptionDisabled,
  isPopperOrSubMenu = false,
  renderTags,
}: NeedlAutocompleteType<T, Multiple>) => {
  const classes = useStyles();
  const showTags = !!renderTags;

  /* https://github.com/mui/material-ui/blob/b3e794791441fa8e6da50ca8545c40d566081f81/packages/material-ui-lab/src/useAutocomplete/useAutocomplete.js#L757
   * tags getting cleared on backspace can't be prevented
   */
  return (
    <Autocomplete<T, Multiple>
      id={id}
      open={open}
      onClose={onClose}
      multiple={multiple}
      disablePortal={disablePortal}
      disableCloseOnSelect={disableCloseOnSelect}
      handleHomeEndKeys={handleHomeEndKeys}
      clearOnBlur={clearOnBlur}
      classes={
        isPopperOrSubMenu
          ? {
              paper: classes.paper,
              option: classes.option,
              popperDisablePortal: classes.popperDisablePortal,
              listbox: classes.listbox,
            }
          : {}
      }
      options={options}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      noOptionsText={noOptionsText}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={(params) => {
        return (
          <div className={classes.inputWrapper} ref={params.InputProps.ref}>
            <InputBase
              {...params.InputProps}
              className={
                showTags
                  ? classes.inputBaseWithTags
                  : classes.inputBaseWithoutTags
              }
              placeholder={placeholder}
              inputProps={params.inputProps}
              classes={{
                input: [
                  classes.inputCommonStyles,
                  showTags ? classes.inputWithTags : classes.inputWithoutTags,
                ].join(' '),
                inputAdornedEnd: classes.inputAdornedEnd,
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              {...(endAdornment
                ? {
                    endAdornment: (
                      <StyledInputAdornment>
                        {endAdornment}
                      </StyledInputAdornment>
                    ),
                  }
                : {})}
            />
          </div>
        );
      }}
    />
  );
};

const StyledInputAdornment = styled(InputAdornment)`
  ${tw`pl-1 pr-5`}
  > svg {
    ${tw`w-5 h-5 fill-current text-gray-400`}
  }
`;

const Chips = ({ chips, chipsCount }: ChipsType) => {
  const remainingItemsCount =
    chips.length - chipsCount > 0 ? chips.length - chipsCount : 0;

  return (
    <>
      <Tags.Tags
        tags={chips.map((value) => ({ value }))}
        tagsCount={chipsCount}
      />
      <ListOverflow count={remainingItemsCount}>
        <Tags.Tags tags={chips.slice(chipsCount).map((value) => ({ value }))} />
      </ListOverflow>
    </>
  );
};

const Checkbox = ({ checked, padding = true }: CheckboxProps) => {
  return <DisplayCheckBox size='large' padding={padding} checked={checked} />;
};

const Radio = ({ checked }: CheckboxProps) => {
  return <DisplayRadio padding checked={checked} />;
};

const AddIcon = React.forwardRef<
  HTMLSpanElement,
  Omit<React.HTMLAttributes<HTMLSpanElement>, 'css'>
>((props, ref) => {
  return (
    <AddIconContainer {...props} ref={ref}>
      <AddSVG />
    </AddIconContainer>
  );
});

const AddIconContainer = styled.span`
  ${tw`p-1.5`}
  > svg {
    ${tw`w-5 h-5 fill-current text-gray-500`}
  }
`;

NeedlAutocomplete.AddIcon = AddIcon;
NeedlAutocomplete.Checkbox = Checkbox;
NeedlAutocomplete.Radio = Radio;
NeedlAutocomplete.Chips = Chips;

const useStyles = makeStyles(() => {
  return {
    inputBaseWithoutTags: {
      width: '100%',
    },
    inputBaseWithTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    inputWrapper: {
      padding: '0.25rem 0.5rem',
      boxShadow: '0 1px 0 0 var(--gray-300)',
      border: '1px solid var(--gray-300)',
      borderRadius: 8,
    },
    inputAdornedEnd: {
      right: '1rem',
    },
    inputCommonStyles: {
      padding: '8px 48px 8px 12px',
      fontSize: 14,
      color: 'var(--gray-900)',
      borderRadius: 8,
      '&:focus': {
        borderColor: 'var(--gray-700)',
      },
      '&:placeholder': {
        color: 'var(--gray-300)',
      },
    },
    inputWithoutTags: {
      border: '1px solid var(--gray-300)',
    },
    inputWithTags: {
      flexShrink: 1,
      width: '80px',
    },
    paper: {
      boxShadow: 'none',
      margin: 0,
      color: '#586069',
      fontSize: 13,
      backgroundColor: 'initial',
    },
    listbox: {
      padding: '0.5rem 1rem',
    },
    popperDisablePortal: {
      position: 'relative',
    },
    option: {
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      borderRadius: 4,
      transition: 'background-color 150ms ease-in-out',
      '&[data-focus="true"], &[aria-selected="true"]': {
        backgroundColor: 'var(--gray-100)',
      },
    },
  };
});

type ChipsType = {
  chips: string[];
  chipsCount: number;
};

type NeedlAutocompleteType<T, Multiple extends boolean> = Omit<
  AutocompleteProps<T, Multiple, undefined, undefined>,
  'renderInput'
> & {
  endAdornment?: React.ReactNode;
  isPopperOrSubMenu?: boolean;
};

type CheckboxProps = { checked: boolean; padding?: boolean };

export type CreatableOptionType = {
  title: React.ReactNode;
  inputValue: string;
};
